import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as Routes from '@/modules/routes';
import Icon from '@/components/ui/Icon';
import SPAButton from '../../shared/SPAButton';
import SPADropdown from '../../shared/SPADropdown';
import { unitEditPath, useContentMount } from '../contentMount';
import { ProgramUnit } from './types';
import UnitNumberDialog from './UnitNumberDialog';

interface UnitActionsMenuProps {
  // The units page offers removal from the program; the table of contents doesn't.
  onRemove?: () => void;
  programId: number;
  unit: ProgramUnit;
}

// Mirrors the legacy admin/UnitActions menu on the unit header. Edit Unit and Edit Unit Lessons
// open the SPA edit unit page (its information and lessons sections); the See Unit links still
// open legacy pages, and Edit Unit Number opens a dialog here. Both pages offer this one menu.
const UnitActionsMenu = ({ onRemove, programId, unit }: UnitActionsMenuProps) => {
  const [numberDialogOpen, setNumberDialogOpen] = useState(false);
  const mount = useContentMount();
  const unitParams = { program_id: programId };

  return (
    <>
      <SPADropdown>
        <SPADropdown.Trigger asChild>
          <SPAButton aria-label={`Actions for ${unit.title_with_position}`} size="xs" variant="outline">
            Actions
            <Icon icon="ChevronDown" size={12} />
          </SPAButton>
        </SPADropdown.Trigger>
        <SPADropdown.Content align="end" className="tw-w-52">
          <SPADropdown.Item asChild>
            <Link to={unitEditPath(programId, unit.id, mount)}>Edit Unit</Link>
          </SPADropdown.Item>
          <SPADropdown.Item onSelect={() => setNumberDialogOpen(true)}>Edit Unit Number</SPADropdown.Item>
          <SPADropdown.Item asChild>
            <Link to={unitEditPath(programId, unit.id, mount, 'lessons')}>Edit Unit Lessons</Link>
          </SPADropdown.Item>
          <SPADropdown.Separator />
          <SPADropdown.Item asChild>
            <a href={Routes.unit_index_admin_notebooks_path({ ...unitParams, unit_id: unit.id })}>
              See Unit Projects
            </a>
          </SPADropdown.Item>
          <SPADropdown.Item asChild>
            <a href={Routes.admin_unit_unit_project_materials_path(unit.id, unitParams)}>See Unit Materials</a>
          </SPADropdown.Item>
          <SPADropdown.Item asChild>
            <a href={Routes.admin_unit_sections_path(unit.id, unitParams)}>See Unit Sections</a>
          </SPADropdown.Item>
          <SPADropdown.Item asChild>
            <a href={Routes.unit_index_admin_unit_slide_shows_path(unit.id, unitParams)}>See Unit Slideshows</a>
          </SPADropdown.Item>
          <SPADropdown.Item asChild>
            <a href={Routes.admin_unit_unit_resources_path(unit.id, unitParams)}>See Unit Resources</a>
          </SPADropdown.Item>
          {onRemove && (
            <>
              <SPADropdown.Separator />
              <SPADropdown.Item className="tw-text-spa-error" onSelect={onRemove}>
                Remove from program
              </SPADropdown.Item>
            </>
          )}
        </SPADropdown.Content>
      </SPADropdown>

      <UnitNumberDialog
        open={numberDialogOpen}
        programId={programId}
        unit={unit}
        onOpenChange={setNumberDialogOpen}
      />
    </>
  );
};

export default UnitActionsMenu;
